import { useState, useEffect } from 'react';
import { AlertTriangle, Clock, ChevronRight, Package, RefreshCw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DLCBadge, DLCIndicator } from './DLCBadge';
import { lotService } from '@/services/lotService';
import { createLogger } from '@/lib/logger';
import { DLC_WARNING_DAYS } from '@/lib/constants';
import type { ExpiringLotSummary, LotStats } from '@/types/inventory.types';

const log = createLogger('ExpiringLots');

interface ExpiringLotsWidgetProps {
  restaurantId: string;
  daysThreshold?: number;
  maxItems?: number;
  onViewAll?: () => void;
  onLotClick?: (lotId: string) => void;
}

export function ExpiringLotsWidget({
  restaurantId,
  daysThreshold = DLC_WARNING_DAYS,
  maxItems = 5,
  onViewAll,
  onLotClick,
}: ExpiringLotsWidgetProps) {
  const [lots, setLots] = useState<ExpiringLotSummary[]>([]);
  const [stats, setStats] = useState<LotStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    if (!restaurantId) return;

    setIsLoading(true);
    setError(null);

    try {
      const [lotsData, statsData] = await Promise.all([
        lotService.getExpiringLots(restaurantId, daysThreshold),
        lotService.getLotStats(restaurantId),
      ]);

      setLots(lotsData.slice(0, maxItems));
      setStats(statsData);
    } catch (err) {
      log.error('Error loading expiring lots', { error: err });
      setError('Impossible de charger les lots');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [restaurantId, daysThreshold]);

  // Si pas de données ou erreur, afficher un état minimal
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="text-orange-500" size={18} />
            DLC à surveiller
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-gray-500">
            <p>{error}</p>
            <Button variant="ghost" size="sm" onClick={loadData} className="mt-2">
              <RefreshCw size={14} />
              Réessayer
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const criticalCount = stats?.critiquesDLC || 0;
  const expiringCount = stats?.expirantBientot || 0;
  const expiredCount = lots.filter(l => l.urgence === 'expire').length;

  return (
    <Card className={criticalCount > 0 ? 'border-orange-300' : ''}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            {criticalCount > 0 ? (
              <AlertTriangle className="text-orange-500" size={18} />
            ) : (
              <Clock className="text-primary" size={18} />
            )}
            DLC à surveiller
          </CardTitle>

          {(criticalCount > 0 || expiringCount > 0) && (
            <div className="flex gap-1">
              {expiredCount > 0 && (
                <Badge variant="danger" size="sm">
                  {expiredCount} périmé{expiredCount > 1 ? 's' : ''}
                </Badge>
              )}
              {criticalCount > 0 && expiredCount === 0 && (
                <Badge variant="warning" size="sm">
                  {criticalCount} critique{criticalCount > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-gray-200" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2 mt-1" />
                </div>
              </div>
            ))}
          </div>
        ) : lots.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <Package size={32} className="mx-auto mb-2 opacity-50" />
            <p>Aucun lot n'expire dans les {daysThreshold} prochains jours</p>
          </div>
        ) : (
          <div className="space-y-2">
            {lots.map((item) => (
              <button
                key={item.lot.id}
                onClick={() => onLotClick?.(item.lot.id)}
                className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors text-left ${
                  item.urgence === 'expire' ? 'bg-red-50 hover:bg-red-100' :
                  item.urgence === 'critique' ? 'bg-orange-50 hover:bg-orange-100' :
                  item.urgence === 'attention' ? 'bg-yellow-50 hover:bg-yellow-100' :
                  'hover:bg-gray-50'
                }`}
              >
                <DLCIndicator dlc={item.lot.dlc} />

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {item.produit_nom}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    Lot {item.lot.numero_lot} • {item.lot.quantite_restante} {item.lot.produit?.unite || 'unité(s)'}
                  </p>
                </div>

                <DLCBadge
                  dlc={item.lot.dlc}
                  joursRestants={item.jours_restants}
                  urgence={item.urgence}
                  size="sm"
                />
              </button>
            ))}

            {expiringCount > maxItems && onViewAll && (
              <button
                onClick={onViewAll}
                className="w-full flex items-center justify-center gap-1 py-2 text-sm text-primary hover:text-primary/80"
              >
                Voir tous les {expiringCount} lots
                <ChevronRight size={14} />
              </button>
            )}
          </div>
        )}

        {/* Stats rapides */}
        {stats && stats.total > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div>
                <p className="font-semibold text-green-600">{stats.actifs}</p>
                <p className="text-xs text-gray-500">Actifs</p>
              </div>
              <div>
                <p className="font-semibold text-orange-600">{stats.expirantBientot}</p>
                <p className="text-xs text-gray-500">Bientôt</p>
              </div>
              <div>
                <p className="font-semibold text-red-600">{stats.perimes}</p>
                <p className="text-xs text-gray-500">Périmés</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Version compacte pour la sidebar ou les petits espaces
export function ExpiringLotsCompact({
  restaurantId,
  onClick,
}: {
  restaurantId: string;
  onClick?: () => void;
}) {
  const [stats, setStats] = useState<LotStats | null>(null);

  useEffect(() => {
    lotService.getLotStats(restaurantId).then(setStats).catch((err) => log.error('Error loading lot stats', { error: err }));
  }, [restaurantId]);

  if (!stats || (stats.critiquesDLC === 0 && stats.expirantBientot === 0)) {
    return null;
  }

  const urgent = stats.critiquesDLC;
  const soon = stats.expirantBientot - stats.critiquesDLC;

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg w-full text-left transition-colors ${
        urgent > 0 ? 'bg-orange-100 hover:bg-orange-200 text-orange-800' :
        'bg-yellow-100 hover:bg-yellow-200 text-yellow-800'
      }`}
    >
      <AlertTriangle size={16} />
      <span className="flex-1 text-sm font-medium">
        {urgent > 0 ? `${urgent} lot${urgent > 1 ? 's' : ''} critique${urgent > 1 ? 's' : ''}` :
         `${soon} lot${soon > 1 ? 's' : ''} à surveiller`}
      </span>
      <ChevronRight size={14} />
    </button>
  );
}
